import type { Category, Service, ServiceInput } from '../../api/client'
import { formatLength, formatPrice, parsePrice, parseWholeNumber, priceInput, priceOrFree } from '../../catalog/format'

// LengthRow is one length as she types it. id is set on a saved length, so
// the API updates it rather than adding a new one.
export interface LengthRow {
  id?: string
  minutes: string
  price: string
}

export interface ServiceForm {
  name: string
  description: string
  lengths: LengthRow[] // in the order she typed them
  buffer_after_minutes: string
  category: Category | ''
  bookable_online: boolean
  form_template_id: string // '' is no form
}

// The same limits and words as the API (catalog.ValidateLengths).
export const MAX_LENGTHS = 8
const MAX_PRICE_PENCE = 100000
const LENGTH_RANGE = 'Choose a length between 5 and 480 minutes, in 5-minute steps.'

export function toServiceForm(service: Service | null): ServiceForm {
  return {
    name: service?.name ?? '',
    description: service?.description ?? '',
    lengths: service
      ? service.lengths.map((l) => ({ id: l.id, minutes: String(l.duration_minutes), price: priceInput(l.price_pence) }))
      : [{ minutes: '60', price: '' }],
    buffer_after_minutes: String(service?.buffer_after_minutes ?? 0),
    category: service?.category ?? '',
    bookable_online: service?.bookable_online ?? true,
    form_template_id: service?.form_template_id ?? '',
  }
}

// toServiceInput returns the request body, or the errors it can spot without
// asking the API. The API still checks every rule. Length errors are keyed
// like the API's (lengths[1].duration_minutes), so they land on their row.
export function toServiceInput(form: ServiceForm): { input: ServiceInput } | { errors: Record<string, string> } {
  const errors: Record<string, string> = {}
  if (form.name.trim() === '') {
    errors.name = 'Enter a name.'
  }
  if (form.lengths.length === 0) {
    errors.lengths = 'A service needs at least one length.'
  } else if (form.lengths.length > MAX_LENGTHS) {
    errors.lengths = `Keep to ${MAX_LENGTHS} lengths or fewer.`
  }
  const lengths: NonNullable<ServiceInput['lengths']> = []
  const seen = new Set<number>()
  form.lengths.forEach((row, i) => {
    const field = `lengths[${i}].`
    // Whole minutes only: "90.5" or "1e2" is refused, not saved as 90 or 1.
    const minutes = parseWholeNumber(row.minutes)
    if (minutes === null || minutes < 5 || minutes > 480 || minutes % 5 !== 0) {
      errors[field + 'duration_minutes'] = LENGTH_RANGE
    } else if (seen.has(minutes)) {
      errors[field + 'duration_minutes'] = `You already have a ${minutes}-minute length.`
    } else {
      seen.add(minutes)
    }
    const price = parsePrice(row.price)
    if (price === null) {
      errors[field + 'price_pence'] = 'Enter a price like 80 or 99.50.'
    } else if (price > MAX_PRICE_PENCE) {
      errors[field + 'price_pence'] = 'Enter a price between £0 and £1,000.'
    }
    if (minutes !== null && price !== null) {
      lengths.push({ ...(row.id ? { id: row.id } : {}), duration_minutes: minutes, price_pence: price })
    }
  })
  const bufferText = form.buffer_after_minutes.trim()
  const buffer = bufferText === '' ? 0 : parseWholeNumber(bufferText)
  if (buffer === null) {
    errors.buffer_after_minutes = 'Enter the blocked time in whole minutes, or 0.'
  }
  if (form.category === '') {
    errors.category = 'Choose a category.'
  }
  if (Object.keys(errors).length > 0 || buffer === null || form.category === '') {
    return { errors }
  }

  const description = form.description.trim()
  return {
    input: {
      name: form.name.trim(),
      description: description === '' ? null : description,
      lengths,
      buffer_after_minutes: buffer,
      category: form.category,
      bookable_online: form.bookable_online,
      form_template_id: form.form_template_id,
    },
  }
}

// lengthSummary is a service's row in the Services list: "60 min · £99" for
// one length, "60 / 90 min · from £99" for several.
export function lengthSummary(service: Service): string {
  const lengths = [...service.lengths].sort((a, b) => a.duration_minutes - b.duration_minutes)
  if (lengths.length === 1) {
    return `${formatLength(lengths[0].duration_minutes)} · ${priceOrFree(lengths[0].price_pence)}`
  }
  const from = Math.min(...lengths.map((l) => l.price_pence))
  const price = lengths.every((l) => l.price_pence === 0) ? 'Free' : `from ${formatPrice(from)}`
  return `${lengths.map((l) => l.duration_minutes).join(' / ')} min · ${price}`
}

// moveId swaps the item at index with its neighbour, if there is one.
export function moveId(ids: string[], index: number, delta: -1 | 1): string[] {
  const target = index + delta
  if (target < 0 || target >= ids.length) {
    return ids
  }
  const next = [...ids]
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}
